import { useEffect, useRef, useState } from "react";
import { Building2, Save, Mic, MicOff, Globe, Loader2, X, Check, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import useSpeechToText from "../lib/useSpeechToText";

const FIELDS = [
  { key: "what_we_do", label: "What does your organisation do?", placeholder: "e.g. We develop and operate rooftop solar and battery-storage projects across India." },
  { key: "what_we_sell", label: "What do you sell / offer?", placeholder: "e.g. Turnkey C&I solar EPC, O&M contracts, and power-purchase agreements." },
  { key: "target_markets", label: "Target markets", placeholder: "e.g. Commercial & industrial buyers in Gujarat, Maharashtra and Tamil Nadu." },
  { key: "goals", label: "What do you want from LaunchRadar?", placeholder: "e.g. Early signals on tenders, policy changes and partners we can approach.", note: "Write this one in your own words. This is what LaunchRadar tunes recommendations around." },
];

export default function Organisation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "org_admin" || user?.role === "orl_admin";
  const [form, setForm] = useState({ what_we_do: "", what_we_sell: "", target_markets: "", goals: "" });
  const [filled, setFilled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const activeKeyRef = useRef(null);
  activeKeyRef.current = activeKey;
  const [website, setWebsite] = useState("");
  const [scraping, setScraping] = useState(false);
  const [autofillPreview, setAutofillPreview] = useState(null);

  const autofillFromWebsite = async () => {
    const url = website.trim();
    if (url.length < 3) { toast.error("Enter your website URL first."); return; }
    setAutofillPreview(null);
    setScraping(true);
    try {
      const r = await api.post("/ai/autofill-org-website", { website: url });
      const d = r.data || {};
      const suggestions = {
        what_we_do: d.what_we_do || "",
        what_we_sell: d.what_we_sell || "",
        target_markets: d.target_markets || "",
      };
      if (Object.values(suggestions).some(Boolean)) {
        setAutofillPreview({
          ...suggestions,
          selected: Object.fromEntries(Object.entries(suggestions).map(([key, value]) => [key, !!value])),
        });
      }
      else toast.error("AI did not return any organisation details.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that website.");
    } finally {
      setScraping(false);
    }
  };

  const applyAutofill = () => {
    if (!autofillPreview) return;
    const selectedSuggestions = Object.fromEntries(
      Object.entries(autofillPreview.selected || {})
        .filter(([key, selected]) => selected && autofillPreview[key])
        .map(([key]) => [key, autofillPreview[key]])
    );
    if (Object.keys(selectedSuggestions).length === 0) {
      toast.error("Select at least one field to apply.");
      return;
    }
    setForm((s) => ({ ...s, ...selectedSuggestions }));
    setAutofillPreview(null);
    toast.success("AI suggestions applied — review and save when ready.");
  };

  const speech = useSpeechToText({
    onResult: (chunk) => {
      const key = activeKeyRef.current;
      if (!key) return;
      setForm((f) => {
        const existing = (f[key] || "").trim();
        const combined = (existing ? existing + " " : "") + chunk;
        return { ...f, [key]: combined.slice(0, 4000) };
      });
    },
  });

  const toggleMic = (key) => {
    if (!speech.supported) {
      toast.error("Speech-to-text is not supported in this browser. Try Chrome, Edge or Safari.");
      return;
    }
    if (speech.listening && activeKey === key) {
      speech.stop();
      setActiveKey(null);
      return;
    }
    if (speech.listening) speech.stop();
    setActiveKey(key);
    speech.start();
  };

  useEffect(() => {
    api.get("/org/context")
      .then((r) => {
        const ctx = r.data?.business_context || {};
        setForm({
          what_we_do: ctx.what_we_do || "",
          what_we_sell: ctx.what_we_sell || "",
          target_markets: ctx.target_markets || "",
          goals: ctx.goals || "",
        });
        setFilled(!!r.data?.context_filled);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/org/context", form);
      setFilled(!!r.data?.context_filled);
      toast.success("Organisation context saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {autofillPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="org-autofill-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAutofillPreview(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#E2E8F0]">
              <div>
                <div className="inline-flex items-center gap-2 text-[#0F1F3D] font-bold text-lg">
                  <Sparkles className="h-4 w-4 text-[#2563EB]" /> AI suggestions
                </div>
                <p className="text-sm text-[#64748B] mt-1">Please verify the AI-generated content before applying it.</p>
              </div>
              <button type="button" onClick={() => setAutofillPreview(null)} data-testid="org-autofill-close" className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-[#F1F5F9]">
                <X className="h-5 w-5 text-[#64748B]" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {FIELDS.slice(0, 3).map((field) => (
                <div key={field.key}>
                  <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-semibold text-[#0F1F3D] mb-1.5">
                    <input
                      type="checkbox"
                      checked={!!autofillPreview.selected?.[field.key]}
                      onChange={() => setAutofillPreview((s) => ({
                        ...s,
                        selected: { ...s.selected, [field.key]: !s.selected?.[field.key] },
                      }))}
                      data-testid={`org-autofill-select-${field.key}`}
                      className="h-4 w-4 accent-[#0F1F3D]"
                    />
                    <span>{field.label}</span>
                  </label>
                  <textarea
                    value={autofillPreview[field.key]}
                    onChange={(e) => setAutofillPreview((s) => ({ ...s, [field.key]: e.target.value.slice(0, 1000) }))}
                    rows={3}
                    maxLength={1000}
                    data-testid={`org-autofill-preview-${field.key}`}
                    className="w-full text-sm px-3 py-2.5 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20"
                  />
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2">
              <button type="button" onClick={() => setAutofillPreview(null)} data-testid="org-autofill-cancel" className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]">Cancel</button>
              <button type="button" onClick={applyAutofill} data-testid="org-autofill-apply" className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-semibold hover:bg-[#172A4F]"><Check className="h-4 w-4" /> Apply suggestions</button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F1F3D] flex items-center gap-2">
          <Building2 className="h-6 w-6 text-[#0F1F3D]" /> About Organisation
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Tell LaunchRadar about your business so explanations and recommended actions
          are tailored to you. You can fill this in now or come back anytime.
        </p>
      </div>

      {loading ? (
        <div className="p-10 text-[#64748B]" data-testid="org-context-loading">Loading…</div>
      ) : (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 space-y-5" data-testid="org-context-form">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${filled ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`}
              data-testid="org-context-status"
            >
              {filled ? "Context complete - Strategic Recommendations enabled" : "Not filled yet — Strategic Recommendations locked"}
            </span>
          </div>

          {/* Voice-first hint */}
          {isAdmin && speech.supported && (
            <div className="rounded-xl border border-[#2563EB]/25 bg-gradient-to-b from-[#EFF6FF] to-white px-4 py-3 flex items-center gap-3" data-testid="org-voice-hint">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#2563EB] text-white shrink-0">
                <Mic className="h-4 w-4" />
              </span>
              <p className="text-sm text-[#475569]">
                Prefer to talk? Tap the mic on any field and describe it out loud — your words fill the box automatically.
              </p>
            </div>
          )}

          {/* Website-based autofill (fills the first 3 fields only) */}
          {isAdmin && (
            <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3.5" data-testid="org-website-autofill">
              <label className="block text-sm font-semibold text-[#0F1F3D] mb-1.5">
                Have a website? Draft the basics from your website. <span className="text-[#94A3B8] font-normal">(optional)</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="e.g. https://www.yourcompany.com"
                  data-testid="org-website-input"
                  className="flex-1 text-sm px-3 py-2.5 rounded-md border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
                />
                <button
                  type="button"
                  onClick={autofillFromWebsite}
                  disabled={scraping}
                  data-testid="org-website-autofill-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1D4ED8] disabled:opacity-60 min-h-[44px] whitespace-nowrap"
                >
                  {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
                  {scraping ? "Reading…" : "Auto-fill from website"}
                </button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-1.5">
                We read your site and draft the first three fields. The last one stays yours to write.
              </p>
            </div>
          )}

          {FIELDS.map((f) => {
            const isThis = speech.listening && activeKey === f.key;
            return (
              <div key={f.key}>
                <label className="block text-sm font-semibold text-[#0F1F3D] mb-1.5">{f.label}</label>
                {f.note && (
                  <p className="text-xs text-[#2563EB] mb-1.5" data-testid={`org-note-${f.key}`}>{f.note}</p>
                )}
                <div className="relative">
                  <textarea
                    value={form[f.key]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value.slice(0, 1000) }))}
                    placeholder={f.placeholder}
                    rows={3}
                    maxLength={1000}
                    disabled={!isAdmin}
                    data-testid={`org-context-${f.key}`}
                    className="w-full text-sm px-3 py-2.5 pr-12 rounded-md border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 disabled:bg-[#F8FAFC]"
                  />
                  {isAdmin && speech.supported && (
                    <button
                      type="button"
                      onClick={() => toggleMic(f.key)}
                      data-testid={`org-mic-${f.key}`}
                      data-listening={isThis ? "true" : "false"}
                      title={isThis ? "Stop dictation" : "Dictate this field"}
                      className={`absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 rounded-full transition-colors ${isThis
                        ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA] ring-2 ring-[#FCA5A5]"
                        : "bg-[#EFF6FF] text-[#2563EB] ring-1 ring-[#2563EB]/30 hover:bg-[#DBEAFE]"}`}
                    >
                      {isThis ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <div className={`text-right text-xs mt-1 tabular-nums ${(form[f.key] || "").length >= 1000 ? "text-[#B91C1C] font-semibold" : "text-[#94A3B8]"}`}>
                  {(form[f.key] || "").length} / 1000
                </div>
                {isThis && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#B91C1C]" data-testid={`org-mic-listening-${f.key}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                    Listening — speak now. Tap the mic to stop.
                  </div>
                )}
              </div>
            );
          })}

          {isAdmin ? (
            <button
              onClick={save}
              disabled={saving}
              data-testid="org-context-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0F1F3D] text-white text-sm font-semibold hover:bg-[#172A4F] disabled:opacity-50 min-h-[44px]"
            >
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save context"}
            </button>
          ) : (
            <p className="text-xs text-[#94A3B8]">Only an organisation admin can edit this.</p>
          )}
        </div>
      )}
    </div>
  );
}
